import {
  candidateProposedVacancyFiltersConfig,
  VacancyList,
  VacancyListFilters,
  type VacancyFiltersQuery,
} from '@features/vacancy';
import { useGetCandidateVacancies } from '@entities/vacancy';
import { useGetCandidate } from '@entities/candidates';
import { PageContainer } from '@shared/ui';
import { PaginateType, type SortQuery } from '@shared/types';
import { useBreadcrumbs } from '@shared/lib';
import { Anchor, Flex, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { NavLink, useParams, useSearchParams } from 'react-router';

export const CandidateProposedVacanciesPage = () => {
  const { id } = useParams();
  const candidateId = id || '';

  const [filter, setFilter] = useState<VacancyFiltersQuery>({});
  const [sort, setSort] = useState<SortQuery>({ id: 'desc' });

  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    perPage: '15',
  });

  const [page, setPage] = useState(Number(searchParams.get('page')));
  const [pageSize, setPageSize] = useState(Number(searchParams.get('perPage')));

  const { data: candidate, isLoading: isCandidateLoading } =
    useGetCandidate(candidateId);

  const {
    data: vacancies,
    isLoading: isVacanciesLoading,
    isFetching: isVacanciesFetching,
  } = useGetCandidateVacancies(candidateId, {
    filter,
    sort,
    perPage: pageSize,
    page,
    paginate: PaginateType.DEFAULT,
  });

  const context = useBreadcrumbs();

  useEffect(() => {
    if (!context || !candidate?.firstName || !candidate?.lastName) {
      return;
    }

    const name = `${candidate.firstName} ${candidate.lastName}`;

    context.setBreadcrumb(
      <Flex gap={4} wrap='nowrap' align='center'>
        <Anchor to='/candidate-list' component={NavLink} variant='gray'>
          Кандидаты
        </Anchor>
        <Text>/</Text>
        <Anchor
          to={`/candidate-list/${candidate.id}`}
          component={NavLink}
          variant='gray'
        >
          {name}
        </Anchor>
        <Text>/</Text>
        <Text c='tertiary.4' truncate='end' maw='40dvw'>
          Предложенные вакансии
        </Text>
      </Flex>
    );

    return () => {
      context.clearBreadcrumb();
    };
  }, [candidate?.firstName, candidate?.id, candidate?.lastName]);

  const handleSortChange = (field: string, direction?: 'asc' | 'desc') => {
    if (!direction) {
      setSort({ id: 'desc' });
      return;
    }

    setSort({
      [field]: direction,
    });
  };

  const isLoading =
    isCandidateLoading || isVacanciesLoading || isVacanciesFetching;

  return (
    <PageContainer title='Предложенные вакансии'>
      <VacancyListFilters
        config={candidateProposedVacancyFiltersConfig}
        setFilter={data => {
          setFilter(data);
          setPage(1);
        }}
      />

      <VacancyList
        vacancies={vacancies?.data || []}
        citySortKey='city.name'
        totalRecords={vacancies?.pagination?.total ?? 0}
        page={page}
        setPage={(next: number) => {
          setPage(next);
          setSearchParams({
            page: next.toString(),
            perPage: pageSize.toString(),
          });
        }}
        pageSize={pageSize}
        setPageSize={(size: number) => {
          setPage(1);
          setPageSize(size);
          setSearchParams({ page: '1', perPage: size.toString() });
        }}
        isFilledFilters={Boolean(Object.keys(filter).length)}
        onSortChange={handleSortChange}
        currentSort={sort}
        isLoading={isLoading}
      />
    </PageContainer>
  );
};
